import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { useProtectedInterval } from '../../hooks/useProtectedInterval';
import {
  setIsUserActive, setNotifierBot, setNotifierBotError, setSupportBot, setSupportBotLink,
  setSupportBotConnected, setSupportBotTopicActive, setSupportBotMode,
  setRates, setUserAvailablePayments, setUserChat,
  setActivePayoutsCount, setActiveRequestsCount, setActiveCredentialsCount,
  setPayoutSettingsEnabled, setHasPayoutSettings, setUsdtBalance,
  setIsFrozen, setFreezeKind, setNovodroidToken, setNovodroidUrl,
  setClientAppTitle, setClientSidebarName, setClientSidebarSubtitle,
  setClientWelcomeMessage, setClientFavicon, setClientBackgroundAnimation,
  setClientBlendMode, setUserRole, setUserRoleIncludeBoost,
  setReduxUserCanChangeAmount, setReduxUserCanSetMinOrders, setUserCanDeleteOwnRequisites,
  setBankCurrencies, setDefaultBankCurrency, setBanksWithoutCard, setBanksWithIban,
  setUnreadNewsCount, setHasRegulation, setIsSuperuser
} from '../../storeRedux';

export default function HeaderDataFetcher() {
  const dispatch = useDispatch();
  const api_url = useSelector((state) => state.API_URL);
  const navigate = useNavigate();

  const getUserInfo = useCallback(() => {
    const jwtToken = localStorage.getItem('accessToken');
    // Парсим is_superuser из JWT claims
    if (jwtToken) {
      try {
        const payload = JSON.parse(atob(jwtToken.split('.')[1]));
        dispatch(setIsSuperuser(!!payload.is_superuser));
      } catch (e) { /* ignore parse errors */ }
    }
    fetch(api_url + 'exchange/managercredentials/user', {
      method: 'GET',
      headers: {
        'Authorization': `JWT ${jwtToken}`,
        'Content-Type': 'application/json'
      }
    })
    .then(response => {
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.clear();
          navigate('/auth');
        } else {
          throw new Error('Network response was not ok');
        }
      }
      return response.json();
    })
    .then(data => {
      dispatch(setIsUserActive(data.active));
      dispatch(setUserAvailablePayments(data.available_payment_settings));
      dispatch(setNotifierBot(data.notifier_bot || ''));
      dispatch(setNotifierBotError(data.notifier_bot_error || ''));
      dispatch(setSupportBot(data.support_bot || ''));
      dispatch(setSupportBotLink(data.support_bot_link || ''));
      dispatch(setSupportBotConnected(data.support_bot_connected || false));
      dispatch(setSupportBotTopicActive(data.support_bot_topic_active || false));
      dispatch(setSupportBotMode(data.support_bot_mode || 'disabled'));
      dispatch(setRates(data.rates));
      dispatch(setUserChat(data.chat));
      dispatch(setActivePayoutsCount(data.active_payouts_count || 0));
      dispatch(setActiveRequestsCount(data.active_requests_count || 0));
      dispatch(setActiveCredentialsCount(data.active_credentials_count || 0));
      dispatch(setPayoutSettingsEnabled(data.payout_settings_enabled || false));
      dispatch(setHasPayoutSettings(data.has_payout_settings || false));
      dispatch(setUsdtBalance(data.usdt_balance || 0));
      dispatch(setIsFrozen(data.is_frozen || false));
      dispatch(setFreezeKind(data.freeze_kind || ''));
      dispatch(setNovodroidToken(data.novodroid_token || ''));
      dispatch(setNovodroidUrl(data.novodroid_url || ''));
      dispatch(setUserCanDeleteOwnRequisites(data.can_delete_own_requisites || false));
      dispatch(setBankCurrencies(data.bank_currencies || {}));
      dispatch(setDefaultBankCurrency(data.default_bank_currency || 'RUB'));
      dispatch(setBanksWithoutCard(data.banks_without_card || []));
      dispatch(setBanksWithIban(data.banks_with_iban || []));
      dispatch(setHasRegulation(data.has_regulation || false));
      // Client settings
      if (data.client_app_title) dispatch(setClientAppTitle(data.client_app_title));
      if (data.client_sidebar_name) dispatch(setClientSidebarName(data.client_sidebar_name));
      if (data.client_sidebar_subtitle) dispatch(setClientSidebarSubtitle(data.client_sidebar_subtitle));
      if (data.client_welcome_message) dispatch(setClientWelcomeMessage(data.client_welcome_message));
      if (data.client_favicon) dispatch(setClientFavicon(data.client_favicon));
      if (data.client_background_animation) dispatch(setClientBackgroundAnimation(data.client_background_animation));
      if (data.client_blend_mode) dispatch(setClientBlendMode(data.client_blend_mode));
      // Dynamic group updates
      if (data.groups) {
        if (data.groups.includes('Тимлид')) {
          dispatch(setUserRole('teamlead'));
        } else if (data.groups.includes('Трейдер')) {
          dispatch(setUserRole('operator'));
        } else if (data.groups.includes('Партнер')) {
          dispatch(setUserRole('partner'));
        }
        dispatch(setUserRoleIncludeBoost(data.groups.includes('BOOST') ? 'yes' : 'no'));
        dispatch(setReduxUserCanChangeAmount(data.groups.includes('CHANGE_AMOUNT') ? 'yes' : 'no'));
        dispatch(setReduxUserCanSetMinOrders(data.groups.includes('MIN_USER_ORDERS') ? 'yes' : 'no'));
      }
      // Update document title and favicon
      if (data.client_app_title) {
        document.title = data.client_app_title;
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
          metaDescription.setAttribute('content', data.client_app_title);
        }
      }
      if (data.client_favicon) {
        let favicon = document.querySelector('link[rel="icon"]');
        if (!favicon) {
          favicon = document.createElement('link');
          favicon.rel = 'icon';
          document.head.appendChild(favicon);
        }
        favicon.href = data.client_favicon;
      }
    })
    .catch(() => {});

    // Fetch unread news count for sidebar badge
    fetch(api_url + 'news/all/?page=1&page_size=1', {
      headers: {
        'Authorization': `JWT ${jwtToken}`,
        'Content-Type': 'application/json',
      },
    })
    .then((r) => r.ok ? r.json() : { unread_count: 0 })
    .then((data) => dispatch(setUnreadNewsCount(data.unread_count || 0)))
    .catch(() => {});
  }, [api_url, dispatch, navigate]);

  useProtectedInterval(getUserInfo, []);

  return null;
}
